/**
 * Pedido do endereço de faturamento no 1º serviço adicional (out/2026,
 * decisão do Bruno): a PF no Free cadastra sem endereço e só o informa quando
 * contrata um serviço — o tomador da NF precisa dele. O backend recusa o
 * pedido com 422 `data.reason = 'billing_address_required'`;
 * `createServiceRequestService` chama `requestBillingAddress()`, que abre este
 * modal (montado uma vez no `App`), e reenvia o pedido depois de salvo. Assim
 * as seis telas que criam solicitações não precisam tratar o caso.
 *
 * Membro de equipe não edita o endereço (é do titular, `isAccountOwner` no
 * backend): o modal só explica.
 */
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { Modal } from 'ui/modal/modal.ui';
import { alert } from 'lib/alert';

import { BillingInfoForm } from 'components/billing-info-form/billing-info-form.component';

import { useAppSelector } from 'store/hooks';
import * as selectUser from 'store/user/user.selector';
import { isAccountOwner } from 'entities/User';
import { AppError } from 'entities/Errors';
import {
  BillingInfo,
  updateOwnBillingInfoService,
} from 'services/billing.service';

type Resolver = (saved: boolean) => void;

let openPrompt: ((resolve: Resolver) => void) | null = null;

/** Abre o modal e resolve `true` quando o endereço foi salvo. */
export const requestBillingAddress = (): Promise<boolean> =>
  new Promise((resolve) => {
    if (!openPrompt) {
      resolve(false);
      return;
    }
    openPrompt(resolve);
  });

const EMPTY: BillingInfo = {
  billingAddress: null,
  billingZipCode: null,
  billingCity: null,
  billingState: null,
};

export const BillingAddressPrompt = () => {
  const currentUser = useAppSelector(selectUser.currentUser);
  const [resolver, setResolver] = useState<Resolver | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    openPrompt = (resolve) => setResolver(() => resolve);
    return () => {
      openPrompt = null;
    };
  }, []);

  const finish = (saved: boolean) => {
    resolver?.(saved);
    setResolver(null);
  };

  const handleSave = async (info: BillingInfo) => {
    setSaving(true);
    try {
      await updateOwnBillingInfoService(info);
      finish(true);
    } catch (err) {
      const error = err as AppError;
      alert.error(
        error?.details?.[0]?.message ||
          error?.message ||
          'Não foi possível salvar o endereço.'
      );
    } finally {
      setSaving(false);
    }
  };

  const owner = currentUser ? isAccountOwner(currentUser) : true;

  return (
    <Modal
      show={!!resolver}
      close={() => finish(false)}
      title="Endereço de faturamento"
      subtitle="Antes de contratar"
      size="lg"
    >
      <Body>
        {owner ? (
          <>
            <p>
              Para contratar um serviço precisamos do endereço que vai na nota
              fiscal. Você só informa uma vez — depois ele fica em{' '}
              <b>Conta → Dados de faturamento</b>.
            </p>
            <BillingInfoForm
              initial={EMPTY}
              onSave={handleSave}
              saving={saving}
              requireComplete
            />
          </>
        ) : (
          <p>
            Para contratar um serviço, o titular da conta precisa informar o
            endereço de faturamento em <b>Conta → Dados de faturamento</b>.
          </p>
        )}
      </Body>
    </Modal>
  );
};

const Body = styled.div`
  padding: 20px 24px 28px;

  p {
    color: ${(props) => props.theme.color.text.primary};
    margin-bottom: 16px;
  }
`;
